import { useState, useContext, useEffect } from 'react';
import { Table, Card, ConfigProvider, Flex, Typography } from 'antd';
import { ReactComponent as New } from '../../../../assets/icons/new.svg';
import { ReactComponent as NewWhite } from '../../../../assets/icons/new-white.svg';
import { ReactComponent as RequestInprogress } from '../../../../assets/icons/ongoing.svg';
import { ReactComponent as RequestInprogressWhite } from '../../../../assets/icons/ongoing-white.svg';
import { ReactComponent as AccessGranted } from '../../../../assets/icons/successful.svg';
import { ReactComponent as AccessGrantedWhite } from '../../../../assets/icons/successful-white.svg';
import { ReactComponent as DarkRightIcon } from '../../../../assets/icons/right-arrow.svg';
import { ReactComponent as LightRightIcon } from '../../../../assets/icons/right-arrow-light.svg';
import { useTheme } from '../../../../context/ThemeContext';
import { API } from '../../../../services';
import { FileAccessDisplay } from '../FileAccessDisplay';
import {
  DARK,
  FILE_ACCESS_STATUS,
  HYPHEN,
  PAGE_KEYS,
  SOS_STATUS_KEYS,
  SOS_STATUS_KEYS_TEXT,
  sosDownloadFormatOptions,
} from '../../../../utilities/constants';
import SOActionModal from './SOActionModal';
import SOResolutionModel from './SOResolutionModel';
import { useTableManager } from '../../../../hooks/logs/useTableManager';
import { useDownloadManager } from '../../../../hooks/logs/useDownloadManager';
import { ErrorMessageContext } from '../../../../context/ErrorMessageContext';
import { getSOSLogsColumns, getSOSLogsExpandedColumns } from '../columnConfig';
import LogsLayout from '../LogsLayout';
import {
  generateTimestampedFilename,
  getDriverNumber,
} from '../../../../utilities/commonFunctions';
import { UserRolesAndPermissionsContext } from '../../../../context/UserRolesAndPermissionsContext';
import Icon from '@ant-design/icons';
import EmptyCard from '../../../../components/EmptyCard/EmptyCard';

const { Text } = Typography;

const SOSLogs = () => {
  const { theme } = useTheme();
  const newIcon = theme === DARK ? New : NewWhite;
  const requestInprogressIcon =
    theme === DARK ? RequestInprogress : RequestInprogressWhite;
  const accessGrantedIcon = theme === DARK ? AccessGranted : AccessGrantedWhite;
  const { addErrorMessage } = useContext(ErrorMessageContext);
  const [isSoActionModalOpen, setIsSoActionModalOpen] = useState(false);
  const [isSoResolutionModelOpen, setIsSoResolutionModelOpen] = useState(false);
  const [clickedSosData, setClickedSosData] = useState('');
  const { isViewOnly } = useContext(UserRolesAndPermissionsContext);
  const [selectedRow, setSelectedRow] = useState(null);
  const [expandedRow, setExpandedRow] = useState(null);

  const {
    logsData,
    setLogsData,
    loading,
    tableParams,
    setTableParams,
    fetchData,
  } = useTableManager(API.getSosReports);

  const { downloadLoading, handleDownload } = useDownloadManager(
    API.getSOSLogsDownload,
    generateTimestampedFilename('sos_logs'),
    addErrorMessage,
    sosDownloadFormatOptions
  );

  const formatData = response => {
    return response?.results?.map((log, index) => ({
      key:
        tableParams?.pagination?.current * tableParams?.pagination?.pageSize +
        index,
      trip_id: log?.trip_id || HYPHEN,
      device_id: log?.device_id || HYPHEN,
      timestamp: log?.timestamp || HYPHEN,
      call_type: log?.call_type || HYPHEN,
      sos_type: log?.sos_type || HYPHEN,
      reporter: log?.reporter || HYPHEN,
      assigned_to: log?.assigned_to || HYPHEN,
      ticket_status: log?.ticket_status || '',
      so_ticket_id: log?.so_ticket_id || HYPHEN,
      drivername: log?.drivername || HYPHEN,
      driver_number: getDriverNumber(log?.driver_number) || HYPHEN,
      description: log?.description || HYPHEN,
      comments: log?.comments || HYPHEN,
      resolved_by: log?.resolved_by || HYPHEN,
      messageid: log?.messageid || HYPHEN,
      cab_registration: log?.cab_registration || HYPHEN,
      access_status: log?.access_status || FILE_ACCESS_STATUS.NO_ACCESS,
      isActionable:
        log?.ticket_status === SOS_STATUS_KEYS.RESOLVED ? false : true,
    }));
  };

  const handleSoActionModalOpen = data => {
    setClickedSosData(data);
    setSelectedRow(data);
    setIsSoActionModalOpen(true);
  };

  const handleSoResolutionModalOpen = data => {
    setClickedSosData(data);
    setSelectedRow(data);
    setIsSoResolutionModelOpen(true);
  };

  useEffect(() => {
    if (!isSoActionModalOpen) {
      setSelectedRow(null);
    }
  }, [isSoActionModalOpen]);

  useEffect(() => {
    if (!isSoResolutionModelOpen) {
      setSelectedRow(null);
    }
  }, [isSoResolutionModelOpen]);

  const updateSOSListOnSOAction = (data, clickedFrom) => {
    if (clickedFrom === 'so-action-modal') {
      setLogsData(prevData =>
        prevData.map(item =>
          item?.messageid === clickedSosData?.messageid
            ? {
                ...item,
                reporter: data?.reporter || HYPHEN,
                call_type: data?.closing_notification?.call_type || HYPHEN,
                sos_type: data?.closing_notification?.reason || HYPHEN,
                description: data?.closing_notification?.description || HYPHEN,
                assigned_to: data?.closing_notification?.assigned_to || HYPHEN,
                ticket_status: data?.closing_notification?.ticket_status,
              }
            : item
        )
      );
    } else if (clickedFrom === 'so-resolution-modal') {
      setLogsData(prevData =>
        prevData.map(item =>
          item?.messageid === clickedSosData?.messageid
            ? {
                ...item,
                ticket_status: data?.closing_notification?.ticket_status,
                comments: data?.closing_notification?.comment || HYPHEN,
                resolved_by: data?.closing_notification?.resolved_by || HYPHEN,
              }
            : item
        )
      );
    }
  };

  const expandedColumns = [...getSOSLogsExpandedColumns()];

  const expandedRowRender = record => {
    return (
      <ConfigProvider
        theme={{
          components: {
            Table: {
              colorBgContainer: 'var(--selected-bg-color, #D0D4D8)',
              headerBg: 'var(--selected-bg-color, #D0D4D8)',
              headerSplitColor: 'transparent',
              colorText: 'var(--selected-text-color, #000000)',
              headerColor: 'var(--selected-text-color, #000000)',
              borderRadius: 0,
              borderRadiusLG: 0,
              borderRadiusSM: 0,
              boxShadowSecondary: 'transparent',
            },
          },
        }}
      >
        <Card variant="borderless" className="sos-logs-row-card">
          <Table
            columns={expandedColumns}
            dataSource={[record]}
            className="expanded-table"
            pagination={false}
            rowHoverable={false}
            scroll={{ x: 'max-content' }}
            rowClassName={() => ''}
            locale={{
              emptyText: <EmptyCard />,
            }}
          />
        </Card>
      </ConfigProvider>
    );
  };

  const getActionColumn = () => ({
    title: 'Actions',
    dataIndex: 'ticket_status',
    key: 'ticket_status',
    sort: true,
    selectFilter: true,
    selectFilterOptions: Object.entries(SOS_STATUS_KEYS)?.map(
      ([key, value]) => ({
        label: SOS_STATUS_KEYS_TEXT[key],
        value: value,
      })
    ),
    width: 192,
    render: (status, data) => {
      if (status === SOS_STATUS_KEYS.PENDING) {
        return (
          <FileAccessDisplay
            access_status="new"
            data={data}
            onClick={() => handleSoActionModalOpen(data)}
            icon={newIcon}
            text="New"
            rightIcon={theme === DARK ? DarkRightIcon : LightRightIcon}
            expanded={expandedRow?.key === data?.key}
          />
        );
      } else if (status === SOS_STATUS_KEYS.IN_PROGRESS) {
        return (
          <FileAccessDisplay
            access_status="in progress"
            data={data}
            onClick={() => handleSoResolutionModalOpen(data)}
            icon={requestInprogressIcon}
            text="In progress"
            rightIcon={theme === DARK ? DarkRightIcon : LightRightIcon}
            expanded={expandedRow?.key === data?.key}
          />
        );
      } else if (status === SOS_STATUS_KEYS.RESOLVED) {
        return (
          <FileAccessDisplay
            access_status="resolved"
            data={data}
            icon={accessGrantedIcon}
            text="Resolved"
            isClickable={false}
            selected={selectedRow?.key === data?.key ? true : false}
            showActionRightIcon={false}
            expanded={expandedRow?.key === data?.key}
          />
        );
      }
    },
  });

  const getStatusColumn = () => ({
    title: 'Status',
    dataIndex: 'ticket_status',
    key: 'ticket_status',
    sort: true,
    selectFilter: true,
    selectFilterOptions: Object.entries(SOS_STATUS_KEYS)?.map(
      ([key, value]) => ({
        label: SOS_STATUS_KEYS_TEXT[key],
        value: value,
      })
    ),
    render: status => {
      if (status === SOS_STATUS_KEYS.PENDING) {
        return (
          <Flex gap={8}>
            <Icon component={newIcon} />
            <Text>New</Text>
          </Flex>
        );
      } else if (status === SOS_STATUS_KEYS.IN_PROGRESS) {
        return (
          <Flex gap={8}>
            <Icon component={requestInprogressIcon} />
            <Text>In progress</Text>
          </Flex>
        );
      } else if (status === SOS_STATUS_KEYS.RESOLVED) {
        return (
          <Flex gap={8}>
            <Icon component={accessGrantedIcon} />
            <Text>Resolved</Text>
          </Flex>
        );
      }
    },
  });

  const columns = [
    ...getSOSLogsColumns(),
    !isViewOnly(PAGE_KEYS.SAFETY_AND_ANOMALY_MONITORING) && getActionColumn(),
    isViewOnly(PAGE_KEYS.SAFETY_AND_ANOMALY_MONITORING) && getStatusColumn(),
  ].filter(Boolean);

  // Set selectedRow to expanded row, or null if collapsed
  const handleExpand = (expanded, record) => {
    if (expanded) {
      setExpandedRow(record);
    } else {
      setExpandedRow(null);
    }
  };

  return (
    <>
      <LogsLayout
        logsData={logsData}
        setLogsData={setLogsData}
        loading={loading}
        tableParams={tableParams}
        setTableParams={setTableParams}
        fetchData={fetchData}
        formatData={formatData}
        columns={columns}
        downloadLoading={downloadLoading}
        handleDownload={handleDownload}
        expandable={true}
        expandedRowRenderCallback={expandedRowRender}
        selectedRow={selectedRow?.key}
        onExpand={handleExpand}
        className="sos-logs-table"
        expandedRow={expandedRow?.key}
      />
      <SOActionModal
        isModalOpen={isSoActionModalOpen}
        setIsModalOpen={setIsSoActionModalOpen}
        clickedSosData={clickedSosData}
        updateSOSList={updateSOSListOnSOAction}
      />
      <SOResolutionModel
        isModalOpen={isSoResolutionModelOpen}
        setIsModalOpen={setIsSoResolutionModelOpen}
        clickedSosData={clickedSosData}
        updateSOSList={updateSOSListOnSOAction}
      />
    </>
  );
};

export default SOSLogs;
